import React, { useState, useEffect, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useSpeechSynthesis } from 'react-speech-kit';
import styles from './AITutor.module.css';
import { API_BASE_URL, apiRequest } from '../../config/api';

const AITutor = ({ currentQuestion, userAnswer, isCorrect, storyContext = null, isDashboard = false }) => {
  const [isListening, setIsListening] = useState(false);
  const [tutorResponse, setTutorResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showInteraction, setShowInteraction] = useState(false);
  const [isWakeWordListening, setIsWakeWordListening] = useState(false);
  const { speak, speaking, cancel } = useSpeechSynthesis();
  const { transcript, resetTranscript } = useSpeechRecognition();
  const animationRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const lastTranscriptRef = useRef('');
  const wakeWordTimerRef = useRef(null);
  const hasSetIntroRef = useRef(false);

  useEffect(() => {
    if(!hasSetIntroRef.current) {
      const hasSeenIntro = localStorage.getItem('tutorIntroduced');
      setTimeout(() => {
        if (!hasSeenIntro) {
        const introMessage = "Hey my name's Nova and I'm your AI tutor. Feel free to say 'Hey Nova!' whenever you want to talk to me";
        setTutorResponse(introMessage);
        setShowInteraction(true);
        setIsSpeaking(true);

        handleSpeak(introMessage, false);
        localStorage.setItem('tutorIntroduced', 'true');
        }
    }, 1000);
    hasSetIntroRef.current = true;
  }
  }, []);

  useEffect(() => {
    setIsSpeaking(speaking);
  }, [speaking]);

  useEffect(() => {
    if (isListening) {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      
      if (transcript !== lastTranscriptRef.current) {
        lastTranscriptRef.current = transcript;
        silenceTimerRef.current = setTimeout(() => {
          if (isListening) {
            handleStopListening();
          }
        }, 1500);
      }
    }
    
    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, [transcript, isListening]);

  const speakHi = () => {
    speak({ 
      text: "Hello! Speak up so I can help you.",
      onEnd: () => {
        handleStartListening();
      }
    });
  };

  useEffect(() => {
    // Wake word detection
    if (isWakeWordListening && (
        transcript.toLowerCase().includes('hi nova') || 
        transcript.toLowerCase().includes('hello nova') || 
        transcript.toLowerCase().includes('hey nova')
      )) {
      SpeechRecognition.stopListening();
      setIsWakeWordListening(false);
      resetTranscript();
      
      // Respond to wake word
      setShowInteraction(true);
      setTutorResponse("Hello! Speak up so I can help you.");
      handleSpeak("Hello! Speak up so I can help you.", false);
      setTimeout(() => {
        handleStartListening();
      }, 3000);
    }
  }, [transcript, isWakeWordListening]);

  // Start wake word detection on component mount
  useEffect(() => {
    startWakeWordDetection();
    
    return () => {
      if (wakeWordTimerRef.current) {
        clearTimeout(wakeWordTimerRef.current);
      }
      if (isWakeWordListening) {
        SpeechRecognition.stopListening();
      }
    };
  }, []);

  const startWakeWordDetection = (listening=null) => {
    console.log("startWakeWordDetection", isListening, isWakeWordListening);
    if (!isListening && !isWakeWordListening) {
      setIsWakeWordListening(true);
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
      
      // Restart wake word detection every 30 seconds to prevent browser issues
      wakeWordTimerRef.current = setTimeout(() => {
        if (isWakeWordListening) {
          SpeechRecognition.stopListening();
          wakeWordTimerRef.current = setTimeout(() => {
            startWakeWordDetection();
          }, 300);
        }
      }, 30000);
    }
    if (listening === false && !isWakeWordListening) {
      console.log("startWakeWordDetection", listening);
      setIsWakeWordListening(true);
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
      
      // Restart wake word detection every 30 seconds to prevent browser issues
      wakeWordTimerRef.current = setTimeout(() => {
        if (isWakeWordListening) {
          SpeechRecognition.stopListening();
          wakeWordTimerRef.current = setTimeout(() => {
            startWakeWordDetection();
          }, 300);
        }
      }, 30000);
    }
  };

  const handleStartListening = () => {
    // Stop wake word detection when manually listening
    if (isWakeWordListening) {
      setIsWakeWordListening(false);
      SpeechRecognition.stopListening();
      if (wakeWordTimerRef.current) {
        clearTimeout(wakeWordTimerRef.current);
      }
    }
    
    resetTranscript();
    setIsListening(true);
    setShowInteraction(true);
    lastTranscriptRef.current = '';
    SpeechRecognition.startListening({ continuous: true });
  };

  const handleStopListening = async () => {
    setIsListening(false);
    setIsProcessing(true);
    SpeechRecognition.stopListening();

    try {
      const endpoint = isDashboard ? 
        `/ai/tutor/dashboard` : 
        `/ai/tutor/help`;

      const data = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          question: currentQuestion,
          userAnswer: userAnswer,
          isCorrect: isCorrect,
          userQuery: transcript,
          storyContext: storyContext ? {
            title: storyContext.title,
            content: storyContext.content,
            relevantParagraph: storyContext.content
          } : null,
          mode: isDashboard ? 'dashboard' : (storyContext ? 'story' : 'practice')
        })
      });
      
      setTutorResponse(data.response);
      // speak({ 
      //   text: data.response,
      //   onEnd: () => {
      //     console.log("Speech ended, starting wake word detection");
      //     setIsSpeaking(false);
      //     startWakeWordDetection();
      //   }
      // });
      handleSpeak(data.response);
    } catch (error) {
      console.error('Tutor response error:', error);
      setTutorResponse('Sorry, I had trouble understanding. Could you try asking again?');
      startWakeWordDetection(false); // Make sure we start wake word detection even if there's an error
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSpeak = (text, startDetection=true) => {
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Enhanced voice customization for smoother, more human-like speech
    utterance.rate = 0.85;  // Slower rate for more natural pacing
    utterance.pitch = 1.05; // Slightly higher pitch but more natural
    utterance.volume = 1.0; // Full volume
    
    // Try to get the most natural voice available
    const voices = window.speechSynthesis.getVoices();
    
    // Prioritize premium natural voices first
    const premiumVoices = voices.filter(voice => 
      (voice.name.includes('Premium') || 
       voice.name.includes('Enhanced') ||
       voice.name.includes('Neural'))
    );
    
    // Then try other natural-sounding voices
    const naturalVoices = voices.filter(voice => 
      (voice.name.includes('Samantha') || 
       voice.name.includes('Google') || 
       voice.name.includes('Natural') ||
       voice.name.includes('Female') ||
       voice.name.includes('Joanna') ||
       voice.name.includes('Siri'))
    );
    
    // Select the best available voice
    if (premiumVoices.length > 0) {
      utterance.voice = premiumVoices[0];
    } else if (naturalVoices.length > 0) {
      utterance.voice = naturalVoices[0];
    }
    
    // Add more sophisticated pauses at punctuation for smoother rhythm
    const processedText = text
      .replace(/([.,!?])\s/g, '$1[pause]')  // Add pause markers after punctuation
      .replace(/([;:])\s/g, '$1[longpause]')  // Longer pauses for semicolons and colons
      .replace(/\[pause\]/g, ' ')  // Replace with actual spaces
      .replace(/\[longpause\]/g, '  ');  // Replace with longer spaces
    
    utterance.text = processedText;
    
    utterance.onend = () => {
      console.log("Speech ended via utterance.onend");
      setIsSpeaking(false);
      if(startDetection) {
        startWakeWordDetection(false);
      }
    };
    
    window.speechSynthesis.speak(utterance);
  };

  const toggleInteraction = () => {
    setShowInteraction(!showInteraction);
    if (isSpeaking) {
      cancel();
      setIsSpeaking(false);
    }
    window.speechSynthesis.cancel(); // Ensure speech is fully canceledxw

    if (showInteraction) {
      startWakeWordDetection(false);
    }
  };

  return (
    <div className={styles.tutorContainer}>
      <div 
        className={styles.animationContainer}
        onClick={toggleInteraction}
        ref={animationRef}
      >
        <div className={`${styles.tutorFace} ${isSpeaking ? styles.speaking : ''} ${isListening ? styles.listening : ''}`}>
          <div className={styles.tutorEyes}>
            <div className={styles.eye}></div>
            <div className={styles.eye}></div>
          </div>
          <div className={styles.tutorMouth}></div>
        </div>
      </div>
      
      {showInteraction && (
        <div className={`${styles.interactionArea} ${styles.visible}`}>
          <button 
            className={`${styles.micButton} ${isListening ? styles.listening : ''}`}
            onClick={isListening ? handleStopListening : handleStartListening}
            disabled={isProcessing}
          >
            {isListening ? 'Stop' : 'Ask Tutor'}
            <span className={styles.micIcon}>🎤</span>
          </button>

          {transcript && (
            <div className={styles.transcriptBox}>
              You: {transcript}
            </div>
          )}

          {tutorResponse && (
            <div className={styles.responseBox}>
              Tutor: {tutorResponse}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITutor;